import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase/firebase.config';
import { useAuth } from '../../../context/AuthContext';
import { ensurePortfolioFieldsHaveNames } from '../../../utils/formNameGenerator';
import { validateSubdomain } from '../../../utils/domain';
import useAppStore from '../../../store/appStore';

const COLLECTION = 'portfolioPages';

const DEFAULT_DATA = {
    displayName: '',
    license: '',
    phone: '',
    email: '',
    avatarImage: '',
    headerImage: '',
    customDomain: '',
    aboutTitle: '',
    aboutBody: '',
    showActiveListings: true,
    customFields: [],
    automations: {
        createLead: true,
        // tags are only applied when createLead is true
        tags: [],
    },
};

/**
 * Normalize domain for comparison:
 * - Lowercase
 * - Trim whitespace
 * - Remove protocol (http://, https://)
 * - Remove www. prefix
 * - Remove trailing slash
 */
function normalizeDomain(domain) {
    if (!domain) return '';
    return domain
        .toLowerCase()
        .trim()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/+$/, '');
}

/**
 * Deep compare two objects for equality
 */
function deepEqual(a, b) {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object') return false;
    if (a === null || b === null) return a === b;

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!deepEqual(a[key], b[key])) return false;
    }

    return true;
}

export function usePortfolioData() {
    const { user } = useAuth();

    // ── Zustand store selectors ──
    const cachedPortfolio = useAppStore((s) => s.ctPortfolioPage);
    const setCtPortfolioPage = useAppStore((s) => s.setCtPortfolioPage);

    // Determine initial state from store
    const hasCachedPortfolio = cachedPortfolio !== null && cachedPortfolio !== undefined;

    const [loading, setLoading] = useState(!hasCachedPortfolio);
    const [saving, setSaving] = useState(false);
    const [docId, setDocId] = useState(hasCachedPortfolio ? (cachedPortfolio.id || cachedPortfolio.docId || null) : null);

    // Saved state (what's in Firestore)
    const [savedData, setSavedData] = useState(hasCachedPortfolio ? cachedPortfolio : DEFAULT_DATA);
    // Draft state (local edits, not yet saved)
    const [draftData, setDraftData] = useState(hasCachedPortfolio ? cachedPortfolio : DEFAULT_DATA);

    // Validation errors
    const [errors, setErrors] = useState({});
    // Save result feedback
    const [saveResult, setSaveResult] = useState(null); // { success: boolean, message?: string }

    // Track if component is mounted to avoid state updates after unmount
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Calculate dirty state by comparing draft to saved
    const isDirty = useMemo(() => {
        return !deepEqual(draftData, savedData);
    }, [draftData, savedData]);

    // Load or create document on mount — cache-aware
    useEffect(() => {
        if (!user?.uid) return;

        // Skip Firestore read if Zustand cache is fresh
        if (hasCachedPortfolio) return;

        setLoading(true);

        (async () => {
            try {
                const q = query(collection(db, COLLECTION), where('ownerId', '==', user.uid));
                const snap = await getDocs(q);

                if (!snap.empty) {
                    const docSnap = snap.docs[0];
                    const loadedData = { ...DEFAULT_DATA, ...docSnap.data(), ownerId: user.uid, id: docSnap.id };
                    setDocId(docSnap.id);
                    setSavedData(loadedData);
                    setDraftData(loadedData);
                    setCtPortfolioPage(loadedData); // write-through to Zustand
                } else {
                    // Create new portfolio document
                    const docRef = await addDoc(collection(db, COLLECTION), {
                        ...DEFAULT_DATA,
                        ownerId: user.uid,
                        createdAt: serverTimestamp(),
                    });
                    const newData = { ...DEFAULT_DATA, ownerId: user.uid, id: docRef.id };
                    setDocId(docRef.id);
                    setSavedData(newData);
                    setDraftData(newData);
                    setCtPortfolioPage(newData); // write-through to Zustand
                }
            } catch (err) {
                console.error('[usePortfolioData] Load error:', err);
            } finally {
                if (mountedRef.current) {
                    setLoading(false);
                }
            }
        })();
    }, [user?.uid]); // eslint-disable-line

    // Clear save result after 3 seconds
    useEffect(() => {
        if (saveResult) {
            const timer = setTimeout(() => {
                if (mountedRef.current) {
                    setSaveResult(null);
                }
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [saveResult]);

    /**
     * Update a single field in draft state (NO Firestore write)
     */
    const setField = useCallback((field, value) => {
        setDraftData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field when user edits
        setErrors(prev => {
            if (prev[field]) {
                const next = { ...prev };
                delete next[field];
                return next;
            }
            return prev;
        });
    }, []);

    /**
     * Update automation setting in draft state (NO Firestore write)
     * Special handling: when createLead is set to false, also clear tags
     */
    const setAutomation = useCallback((key, value) => {
        setDraftData(prev => {
            const newAutomations = { ...(prev.automations || {}), [key]: value };

            // If createLead is being disabled, clear tags as well
            if (key === 'createLead' && !value) {
                newAutomations.tags = [];
            }

            return {
                ...prev,
                automations: newAutomations
            };
        });
    }, []);

    /**
     * Update automation tags in draft state (NO Firestore write)
     * Only effective when createLead is enabled
     */
    const setAutomationTags = useCallback((tags) => {
        setDraftData(prev => ({
            ...prev,
            automations: { ...(prev.automations || {}), tags: tags || [] }
        }));
    }, []);

    /**
     * Update custom fields in draft state (NO Firestore write)
     */
    const setCustomFields = useCallback((fields) => {
        const fieldsWithNames = ensurePortfolioFieldsHaveNames(fields);
        setDraftData(prev => ({ ...prev, customFields: fieldsWithNames }));
    }, []);

    /**
     * Discard all draft changes and revert to saved state
     */
    const discardChanges = useCallback(() => {
        setDraftData(savedData);
        setErrors({});
        setSaveResult(null);
    }, [savedData]);

    /**
     * Validate draft data before saving
     * @returns {{ valid: boolean, errors: Object }}
     */
    const validateDraft = useCallback(() => {
        const newErrors = {};

        // Validate customDomain if set
        if (draftData.customDomain) {
            const normalized = normalizeDomain(draftData.customDomain);

            // Check if it's a subdomain (no dots) - needs format validation
            if (!normalized.includes('.')) {
                const validation = validateSubdomain(normalized);
                if (!validation.valid) {
                    newErrors.customDomain = validation.error;
                }
            }
        }

        setErrors(newErrors);
        return { valid: Object.keys(newErrors).length === 0, errors: newErrors };
    }, [draftData]);

    /**
     * Save changes to Firestore via Cloud Function
     * Validates domain uniqueness server-side
     */
    const saveChanges = useCallback(async () => {
        if (!docId || !isDirty) {
            return { success: true };
        }

        // Client-side validation first
        const validation = validateDraft();
        if (!validation.valid) {
            setSaveResult({ success: false, message: 'Please fix validation errors' });
            return { success: false, errors: validation.errors };
        }

        setSaving(true);
        setSaveResult(null);
        setErrors({});

        try {
            // Calculate what changed (only send delta)
            const updates = {};
            for (const key of Object.keys(draftData)) {
                if (key === 'ownerId' || key === 'id' || key === 'createdAt') continue;
                if (!deepEqual(draftData[key], savedData[key])) {
                    updates[key] = draftData[key];
                }
            }

            // Normalize domain before sending
            if ('customDomain' in updates) {
                updates.customDomain = normalizeDomain(updates.customDomain);
            }

            // Call Cloud Function for validated update
            const updatePortfolioPage = httpsCallable(functions, 'updatePortfolioPage');
            const response = await updatePortfolioPage({ docId, updates });
            const result = response.data;

            if (result.success) {
                // Update saved state to match draft
                setSavedData({ ...draftData });
                setCtPortfolioPage({ ...draftData }); // write-through to Zustand
                setSaveResult({ success: true, message: 'Changes saved successfully' });
                return { success: true };
            } else {
                // Server-side validation failed (e.g., domain taken)
                if (result.field) {
                    setErrors({ [result.field]: result.error });
                }
                setSaveResult({ success: false, message: result.error || 'Save failed' });
                return { success: false, error: result.error, field: result.field };
            }
        } catch (err) {
            console.error('[usePortfolioData] Save error:', err);
            const message = err.message || 'Failed to save changes';
            setSaveResult({ success: false, message });
            return { success: false, error: message };
        } finally {
            if (mountedRef.current) {
                setSaving(false);
            }
        }
    }, [docId, isDirty, draftData, savedData, validateDraft]);

    /**
     * Update image field after successful upload
     * This is called by the upload hook AFTER Firestore write completes
     * So we need to sync both draft and saved state
     */
    const updateImageField = useCallback((field, url) => {
        setDraftData(prev => ({ ...prev, [field]: url }));
        setSavedData(prev => ({ ...prev, [field]: url }));
    }, []);

    /**
     * Check if the current customDomain value is available.
     * Calls the checkDomainAvailability Cloud Function.
     * @returns {{ available: boolean, message?: string, error?: string }}
     */
    const checkDomainAvailability = useCallback(async () => {
        const domain = draftData.customDomain;
        if (!domain || !domain.trim()) {
            setErrors(prev => ({ ...prev, customDomain: 'Please enter a domain to check' }));
            return { available: false, error: 'Please enter a domain to check' };
        }

        const normalized = normalizeDomain(domain);

        // Client-side format validation for subdomains
        if (!normalized.includes('.')) {
            const validation = validateSubdomain(normalized);
            if (!validation.valid) {
                setErrors(prev => ({ ...prev, customDomain: validation.error }));
                return { available: false, error: validation.error };
            }
        }

        try {
            const checkFn = httpsCallable(functions, 'checkDomainAvailability');
            const response = await checkFn({ domain: normalized });
            const result = response.data;

            if (result.available) {
                // Clear any previous domain error
                setErrors(prev => {
                    const next = { ...prev };
                    delete next.customDomain;
                    return next;
                });
                return { available: true, message: result.message };
            } else {
                setErrors(prev => ({ ...prev, customDomain: result.error }));
                return { available: false, error: result.error };
            }
        } catch (err) {
            console.error('[usePortfolioData] Domain check error:', err);
            const message = err.message || 'Failed to check domain availability';
            setErrors(prev => ({ ...prev, customDomain: message }));
            return { available: false, error: message };
        }
    }, [draftData.customDomain]);

    return {
        loading,
        saving,
        docId,
        data: draftData,           // Draft data for UI
        savedData,                 // Saved data for reference
        isDirty,                   // True if draft differs from saved
        errors,                    // Field-level validation errors
        saveResult,                // { success, message } after save attempt
        setField,                  // Update single field (draft only)
        setAutomation,             // Update automation setting (draft only)
        setAutomationTags,         // Update automation tags (draft only)
        setCustomFields,           // Update custom fields (draft only)
        saveChanges,               // Explicit save to Firestore
        discardChanges,            // Revert draft to saved state
        updateImageField,          // Update image after upload
        checkDomainAvailability,   // Check if domain is available
    };
}
